import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Calendar, Clock, User, Video, Link, Users, X, ExternalLink, Loader2, Eye, Clipboard } from "lucide-react";
import { Modal } from "../../components/ui/Modal";
import Pagination from "../../components/ui/Pagination";
import { useSlotsByInterviewer, useMeetingLinkStatus, useStoreMeetingLink } from "../../hooks/queries";
import useAuth from "../../hooks/stores/useAuth";
import { convertUTCToLocalTime } from "../../utils/timeUtils";
import { toast } from "sonner";

// Import platform logos
import GoogleMeetLogo from "../../assets/logo/google-meet-icon-2020-.svg";
import ZoomLogo from "../../assets/logo/zoomus-ar21~bgwhite.svg";
import TeamsLogo from "../../assets/logo/Microsoft_Office_Teams_Logo.svg";

export default function RemoteCallManagement() {
  const [selectedCall, setSelectedCall] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [meetingLink, setMeetingLink] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 10;
  const navigate = useNavigate();

  // Get current interviewer ID from auth
  const { userId } = useAuth();

  // React Query hooks
  const { 
    data: remoteCallsData, 
    isLoading: isLoadingCalls, 
    error: callsError,
    refetch
  } = useSlotsByInterviewer(userId, currentPage, perPage);

  const { 
    data: statusData, 
    isLoading: isLoadingStats, 
    error: statsError 
  } = useMeetingLinkStatus();

  const storeMeetingLinkMutation = useStoreMeetingLink();

  // Extract data from response structure
  const remoteCalls = remoteCallsData?.slots?.data || [];
  const pagination = remoteCallsData?.slots || {};
  const statusStats = {
    pending: statusData?.data?.links_remaining || 0,
    link_added: statusData?.data?.links_added || 0
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        weekday: 'short',
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    } catch (error) {
      return dateString;
    }
  };

  const formatTime = (utcTimeString, dateString) => {
    return convertUTCToLocalTime(utcTimeString, dateString);
  };

  // Simplified status mapping - only pending and link_added
  const getCallStatus = (call) => {
    // If payment is pending, user cannot proceed with meeting link
    if (call.payment_status === "pending") {
      return "payment_pending";
    }
    
    if (!call.meeting_link) {
      return "pending";
    } else {
      return "link_added";
    }
  };

  const getStatusBadge = (call) => {
    const status = getCallStatus(call);
    const statusConfig = {
      payment_pending: { bg: "bg-red-100", text: "text-red-800", label: "Payment Pending" },
      pending: { bg: "bg-orange-100", text: "text-orange-800", label: "Pending Link" },
      link_added: { bg: "bg-green-100", text: "text-green-800", label: "Link Added" }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  const getPlatformIcon = (platform) => {
    return platform === "teams" || platform === "microsoft" ? <Users size={16} /> : <Video size={16} />;
  };

  const getPlatformName = (platform) => {
    const platforms = {
      "google": "Google Meet",
      "zoom": "Zoom",
      "teams": "Microsoft Teams",
      "microsoft": "Microsoft Teams"
    };
    return platforms[platform] || "Google Meet";
  };

  // Get dynamic label for meeting link based on platform
  const getPlatformLinkLabel = (platform) => {
    const labels = {
      "google": "Google Meet Link",
      "zoom": "Zoom Link", 
      "teams": "Microsoft Teams Link",
      "microsoft": "Microsoft Teams Link"
    };
    return labels[platform] || "Meeting Link";
  };

  // Get platform URL for generating meeting links
  const getPlatformUrl = (platform) => {
    const urls = {
      "google": "https://meet.google.com/",
      "zoom": "https://zoom.us/start/videomeeting",
      "teams": "https://teams.microsoft.com/",
      "microsoft": "https://teams.microsoft.com/"
    };
    return urls[platform] || "https://meet.google.com/";
  };

  // Get platform logo/icon (using actual platform logos)
  const getPlatformLogo = (platform) => {
    const logos = {
      "google": { 
        logo: GoogleMeetLogo, 
        text: "Google Meet", 
        alt: "Google Meet Logo",
        bgClass: "bg-white"
      },
      "zoom": { 
        logo: ZoomLogo, 
        text: "Zoom", 
        alt: "Zoom Logo",
        bgClass: "bg-white"
      },
      "teams": { 
        logo: TeamsLogo, 
        text: "Microsoft Teams", 
        alt: "Microsoft Teams Logo",
        bgClass: "bg-white"
      },
      "microsoft": { 
        logo: TeamsLogo, 
        text: "Microsoft Teams", 
        alt: "Microsoft Teams Logo",
        bgClass: "bg-white"
      }
    };
    return logos[platform] || logos.google;
  };

  // Handle opening platform URL
  const handleOpenPlatform = (platform) => {
    const url = getPlatformUrl(platform);
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  // Handle pasting from clipboard
  const handlePasteLinkold = async (e) => {
    try {
      console.log("handlePasteLink", e);
      // if (navigator.clipboard && navigator.clipboard.readText) {
      //   const text = await navigator.clipboard.readText();
      //   if (text.trim()) {
      //     setMeetingLink(text.trim());
      //     toast.success("Link pasted successfully!");
      //   } else {
      //     toast.error("Clipboard is empty");
      //   }
      // } else {
      //   // Fallback for browsers that don't support clipboard API
      //   toast.error("Clipboard access not supported. Please paste manually using Ctrl+V");
      // }
    } catch (error) {
      // Handle permission denied or other errors
      console.error('Clipboard access failed:', error);
      toast.error("Clipboard access denied. Please paste manually using Ctrl+V");
    }
  };

  const handlePasteLink = async () => {
    try {
      
      if (navigator.clipboard && navigator.clipboard.readText) {
        const text = await navigator.clipboard.readText();
        if (text.trim()) {
          setMeetingLink(text.trim());
          toast.success("Link pasted successfully!");
        } else {
          toast.error("Clipboard is empty");
        }
      } else {
        // Fallback for browsers that don't support clipboard API
        toast.error("Clipboard access not supported. Please paste manually using Ctrl+V");
      }
    } catch (error) {
      // Handle permission denied or other errors
      console.error('Clipboard access failed:', error);
      toast.error("Clipboard access denied. Please paste manually using Ctrl+V");
    }
  };

  const viewClientDetails = (call) => {
    console.log('Viewing client details for call:', call);
    const userId = call.user_id;
    if (userId) {
      navigate(`/user-questions/${userId}`, {
        state: { 
          returnTo: 'remote-calls',
          returnPath: '/interviewer-dashboard',
          activeTab: 'remote-calls'
        }
      });
    } else {
      console.error('No user ID found for call:', call);
    }
  };

  const handleAddLink = (call) => {
    setSelectedCall(call);
    setMeetingLink(call.meeting_link || "");
    setIsModalOpen(true);
  };

  const handleSaveLink = async () => {
    if (!meetingLink.trim()) {
      toast.error("Please enter a meeting link");
      return;
    }

    // Debug logging
    console.log('Selected Call Object:', selectedCall);
    console.log('Slot ID being sent:', selectedCall?.id);
    console.log('Meeting Link being sent:', meetingLink.trim());

    try {
      await storeMeetingLinkMutation.mutateAsync({
        slotId: selectedCall.id,
        meetingLink: meetingLink.trim()
      });

      toast.success("Meeting Link Saved & Confirmed", {
        description: `Meeting link added successfully for ${selectedCall.user?.name || 'the client'}. An email notification has been sent.`,
        duration: 4000,
      });

      setIsModalOpen(false);
      setSelectedCall(null);
      setMeetingLink("");
    } catch (error) {
      console.error('Error saving meeting link:', error);
      toast.error("Failed to Save Link", {
        description: error.response?.data?.message || "Something went wrong. Please try again.",
        duration: 4000,
      });
    }
  };

  // Meeting link management buttons
  const renderMeetingLinkButtons = (call) => {
    const status = getCallStatus(call);
    return (
      <div className="flex flex-wrap items-center gap-2">
        {status === "payment_pending" && (
          <button
            disabled={true}
            className="text-gray-400 text-sm font-medium flex items-center px-3 py-1.5 border border-gray-200 rounded-md cursor-not-allowed opacity-50"
            title="Payment is pending. Link can be added once payment is completed."
          >
            <Link className="mr-1" size={14} />
            Add Link
          </button>
        )}
        {status === "pending" && (
          <button
            onClick={() => handleAddLink(call)}
            disabled={storeMeetingLinkMutation.isPending}
            className="text-primary hover:text-primary/80 text-sm font-medium flex items-center px-3 py-1.5 border border-primary/20 rounded-md hover:bg-primary/5 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Link className="mr-1" size={14} />
            Add Link
          </button>
        )}
        {status === "link_added" && call.meeting_link && (
          <a
            href={call.meeting_link}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:text-primary/80 text-sm font-medium flex items-center px-3 py-1.5 border border-primary/20 rounded-md hover:bg-primary/5"
          >
            <ExternalLink className="mr-1" size={14} />
            Open Link
          </a>
        )}
      </div>
    );
  };

  // View questions button
  const renderQuestionButton = (call) => {
    return (
      <button
        onClick={() => viewClientDetails(call)}
        className="text-primary hover:text-primary/80 text-sm font-medium flex items-center px-3 py-1.5 border border-primary/20 rounded-md hover:bg-primary/5"
      >
        <Eye className="mr-1" size={14} />
        View Questions
      </button>
    );
  };

  if (isLoadingCalls || isLoadingStats) {
    return (
      <div className="p-3 sm:p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-secondary rounded-lg w-1/4 mb-6"></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-6">
            {[...Array(2)].map((_, i) => (
              <div key={i} className="h-16 bg-secondary rounded-lg"></div>
            ))}
          </div>
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-16 bg-secondary rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (callsError || statsError) {
    return (
      <div className="p-3 sm:p-6">
        <div className="text-center">
          <p className="mb-4 text-foreground">{callsError?.message || statsError?.message}</p>
          <button 
            onClick={() => refetch()} 
            className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-6">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-xl sm:text-2xl font-bold text-foreground mb-2">Remote Call Requests</h2>
        <p className="text-sm sm:text-base text-muted-foreground">
          Manage remote interview sessions and add meeting links for clients ({pagination.total || 0} total)
        </p>
      </div>

      {/* Stats Cards - Updated to only show 2 stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-6">
        <div className="bg-secondary border border-secondary-border rounded-lg p-3 sm:p-4">
          <div className="flex items-center">
            <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center">
              <Clock className="text-white" size={16} />
            </div>
            <div className="ml-3">
              <div className="text-lg font-bold text-foreground">
                {isLoadingStats ? <div className="w-6 h-6 animate-spin rounded-full border-2 border-muted border-t-foreground"></div> : statusStats.pending}
              </div>
              <div className="text-xs text-muted-foreground">Pending Links</div>
            </div>
          </div>
        </div>
        
        <div className="bg-secondary border border-secondary-border rounded-lg p-3 sm:p-4">
          <div className="flex items-center">
            <div className="w-8 h-8 bg-green-500 rounded-lg flex items-center justify-center">
              <Link className="text-white" size={16} />
            </div>
            <div className="ml-3">
              <div className="text-lg font-bold text-foreground">
                {isLoadingStats ? <div className="w-6 h-6 animate-spin rounded-full border-2 border-muted border-t-foreground"></div> : statusStats.link_added}
              </div>
              <div className="text-xs text-muted-foreground">Links Added</div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Card Layout (hidden on lg and up) */}
      <div className="lg:hidden space-y-4">
        {isLoadingCalls ? (
          <div className="bg-secondary border border-secondary-border rounded-lg p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading remote calls...</p>
          </div>
        ) : remoteCalls.length === 0 ? (
          <div className="bg-secondary border border-secondary-border rounded-lg p-8 text-center">
            <Video className="mx-auto mb-2 text-muted-foreground" size={32} />
            <p className="text-muted-foreground">No remote call requests found</p>
          </div>
        ) : (
          remoteCalls.map((call) => (
            <div key={call.id} className="bg-secondary border border-secondary-border rounded-lg p-4">
              {/* Client Info */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                    <User className="text-primary" size={16} />
                  </div>
                  <div className="ml-3">
                    <div className="font-medium text-foreground">{call.user?.name || `Client #${call.user_id}`}</div>
                    <div className="text-sm text-muted-foreground">{call.user?.email || `ID: ${call.user_id}`}</div>
                  </div>
                </div>
                {getStatusBadge(call)}
              </div>

              {/* Details Grid */}
              <div className="grid grid-cols-2 gap-3 mb-4 text-sm">
                <div>
                  <div className="flex items-center text-muted-foreground mb-1">
                    <Calendar className="mr-2" size={12} />
                    <span className="text-xs">Date</span>
                  </div>
                  <div className="text-foreground font-medium">{formatDate(call.slot_date)}</div>
                </div>
                
                <div>
                  <div className="flex items-center text-muted-foreground mb-1">
                    <Clock className="mr-2" size={12} />
                    <span className="text-xs">Time</span>
                  </div>
                  <div className="text-foreground font-medium">
                    {formatTime(call.slot_time, call.slot_date)}
                    {call.end_time && (
                      <span className="text-xs text-muted-foreground ml-1">
                        - {formatTime(call.end_time, call.slot_date)}
                      </span>
                    )}
                  </div>
                </div>

                <div>
                  <div className="flex items-center text-muted-foreground mb-1">
                    {getPlatformIcon(call.preferred_platform)}
                    <span className="ml-2 text-xs">Platform</span>
                  </div>
                  <div className="text-foreground font-medium">{getPlatformName(call.preferred_platform)}</div>
                </div>

                <div>
                  <div className="flex items-center text-muted-foreground mb-1">
                    <Clock className="mr-2" size={12} />
                    <span className="text-xs">Duration</span>
                  </div>
                  <div className="text-foreground font-medium">
                    {call.duration ? `${call.duration} min` : 'N/A'}
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="pt-3 border-t border-secondary-border">
                <div className="space-y-2">
                  <div>
                    <div className="text-xs text-muted-foreground mb-1">Call Link</div>
                    {renderMeetingLinkButtons(call)}
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground mb-1">Actions</div>
                    {renderQuestionButton(call)}
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Desktop Table Layout (hidden on mobile/tablet) */}
      <div className="hidden lg:block bg-secondary border border-secondary-border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-secondary border-b border-secondary-border">
              <tr>
                <th className="text-left py-3 px-4 font-semibold text-foreground">Client</th>
                <th className="text-left py-3 px-4 font-semibold text-foreground">Date & Time (Local)</th>
                <th className="text-left py-3 px-4 font-semibold text-foreground">Platform</th>
                <th className="text-left py-3 px-4 font-semibold text-foreground">Duration</th>
                <th className="text-left py-3 px-4 font-semibold text-foreground">Status</th>
                <th className="text-left py-3 px-4 font-semibold text-foreground">Call Link</th>
                <th className="text-left py-3 px-4 font-semibold text-foreground">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-secondary-border">
              {isLoadingCalls ? (
                <tr>
                  <td colSpan="7" className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
                    <p className="text-muted-foreground">Loading remote calls...</p>
                  </td>
                </tr>
              ) : remoteCalls.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center py-8">
                    <Video className="mx-auto mb-2 text-muted-foreground" size={32} />
                    <p className="text-muted-foreground">No remote call requests found</p>
                  </td>
                </tr>
              ) : (
                remoteCalls.map((call) => (
                  <tr key={call.id} className="hover:bg-secondary/50">
                    <td className="py-3 px-4">
                      <div className="flex items-center">
                        <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                          <User className="text-primary" size={14} />
                        </div>
                        <div className="ml-3">
                          <div className="font-medium text-foreground">{call.user?.name || `Client #${call.user_id}`}</div>
                          <div className="text-xs text-muted-foreground">{call.user?.email || `ID: ${call.user_id}`}</div>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="text-sm">
                        <div className="flex items-center text-foreground">
                          <Calendar className="mr-2 text-primary" size={14} />
                          {formatDate(call.slot_date)}
                        </div>
                        <div className="flex items-center text-muted-foreground mt-1">
                          <Clock className="mr-2" size={12} />
                          {formatTime(call.slot_time, call.slot_date)}
                          {call.end_time && (
                            <span className="text-xs ml-1">
                              - {formatTime(call.end_time, call.slot_date)}
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center text-sm">
                        <div className="text-primary mr-2">
                          {getPlatformIcon(call.preferred_platform)}
                        </div>
                        <span className="text-foreground">{getPlatformName(call.preferred_platform)}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="text-sm text-foreground">
                        {call.duration ? `${call.duration} min` : 'N/A'}
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      {getStatusBadge(call)}
                    </td>
                    <td className="py-3 px-4">
                      {renderMeetingLinkButtons(call)}
                    </td>
                    <td className="py-3 px-4">
                      {renderQuestionButton(call)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      <div className="bg-secondary border border-secondary-border rounded-lg mb-6">
        <div className="border-t border-secondary-border">
          <Pagination
            currentPage={pagination.current_page || 1}
            lastPage={pagination.last_page || 1}
            perPage={pagination.per_page || 10}
            total={pagination.total || 0}
            onPageChange={handlePageChange}
          />
        </div>
      </div>

      {/* Add Meeting Link Modal */}
      <Modal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
        title="Add Meeting Link"
        className="max-w-md sm:max-w-lg mx-4"
      >
        {selectedCall && (
          <div className="mb-4 p-3 sm:p-4 bg-secondary border border-secondary-border rounded-lg">
            <div className="text-sm">
              <div className="font-medium text-foreground mb-1">{selectedCall.user.name}</div>
              <div className="text-muted-foreground mb-2">{selectedCall.user.email}</div>
              <div className="flex items-center text-muted-foreground mb-1">
                <Calendar className="mr-2" size={12} />
                {formatDate(selectedCall.slot_date)} at {formatTime(selectedCall.slot_time, selectedCall.slot_date)}
              </div>
              <div className="flex items-center text-muted-foreground">
                {getPlatformIcon(selectedCall.preferred_platform)}
                <span className="ml-2">{getPlatformName(selectedCall.preferred_platform)}</span>
              </div>
            </div>
          </div>
        )}

        {/* Platform Link Generation Section */}
        {selectedCall && (
          <div className="mb-4 p-3 sm:p-4 bg-background border border-secondary-border rounded-lg">
            <div className="text-sm font-medium text-foreground mb-3">Generate Meeting Link</div>
            <button
              onClick={() => handleOpenPlatform(selectedCall.preferred_platform)}
              className="w-full flex items-center gap-4 p-4 border border-secondary-border rounded-lg hover:bg-secondary/50 transition-colors group"
            >
              <div className="flex items-center gap-3">
                {(() => {
                  const platformLogo = getPlatformLogo(selectedCall.preferred_platform);
                  return (
                    <div className={`w-12 h-12 ${platformLogo.bgClass} rounded-lg flex items-center justify-center p-2 group-hover:scale-105 transition-transform`}>
                      <img 
                        src={platformLogo.logo} 
                        alt={platformLogo.alt}
                        className="w-full h-full object-contain"
                      />
                    </div>
                  );
                })()}
                <div className="text-left">
                  <div className="font-semibold text-foreground">
                    {getPlatformLogo(selectedCall.preferred_platform).text}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Click to create meeting
                  </div>
                </div>
              </div>
              <div className="flex-1"></div>
              <ExternalLink className="text-muted-foreground group-hover:text-foreground transition-colors" size={18} />
            </button>
            <div className="text-xs text-muted-foreground mt-2 text-center">
              Opens {getPlatformName(selectedCall.preferred_platform)} in a new tab to generate your meeting link
            </div>
          </div>
        )}

        <div className="mb-4">
          <label htmlFor="meetingLink" className="block text-sm font-medium text-foreground mb-2">
            {selectedCall ? getPlatformLinkLabel(selectedCall.preferred_platform) : "Meeting Link"}
          </label>
          <div className="flex gap-2">
            <input
              id="meetingLink"
              type="url"
              placeholder={selectedCall?.preferred_platform === "zoom" 
                ? "https://zoom.us/j/1234567890" 
                : selectedCall?.preferred_platform === "teams" || selectedCall?.preferred_platform === "microsoft"
                ? "https://teams.microsoft.com/l/meetup-join/..."
                : "https://meet.google.com/abc-defg-hij"
              }
              className="flex-1 px-3 py-2 bg-background border border-secondary-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary text-sm"
              value={meetingLink}
              onChange={(e) => setMeetingLink(e.target.value)}
            />
            <button
              onClick={handlePasteLink}
              className="px-3 py-2 bg-secondary border border-secondary-border rounded-md hover:bg-secondary/80 transition-colors flex items-center gap-1.5 text-sm"
              title="Paste from clipboard"
            >
              <Clipboard size={14} />
              <span className="hidden sm:inline">Paste</span>
            </button>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-3">
          <button
            onClick={() => setIsModalOpen(false)}
            className="w-full sm:w-auto px-4 py-2 text-muted-foreground hover:text-foreground border border-secondary-border rounded-md order-2 sm:order-1"
          >
            Cancel
          </button>
          <button
            onClick={handleSaveLink}
            disabled={!meetingLink.trim() || storeMeetingLinkMutation.isPending}
            className="w-full sm:w-auto px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed order-1 sm:order-2 flex items-center justify-center"
          >
            {storeMeetingLinkMutation.isPending ? (
              <>
                <Loader2 className="mr-2 animate-spin" size={16} />
                Saving...
              </>
            ) : (
              'Save & Confirm Link'
            )}
          </button>
        </div>
      </Modal>
    </div>
  );
} 